/**
 * The one definition of a request's region (#1507, from the manual test pass).
 *
 * ⚠️ WHY THIS EXISTS. `requests.region` was being compared six different ways,
 * and a NAM supervisor stopped receiving her digest because of it. The spread,
 * in our own database: `NAM` 186 rows, `nam` 25, `North America` 7, the
 * `[BLANK]` import sentinel 29. Four spellings, written by at least three
 * different code paths, of which the dropdown — which writes LOWER case — is
 * only 25 of them.
 *
 * ⚠️ ALIASES NORMALISE ON WRITE. READS COMPARE CANONICAL TOKENS. This is the
 * whole design and it is worth stating before the exports, because the obvious
 * alternative is worse: teaching every reader `region IN ('NAM','NORTH
 * AMERICA','NA')` would make the UNION of two already-disagreeing alias tables
 * permanently grantable — this file's and `requestSchema.json`'s `x-hint`, which
 * between them claim `NA`, `EU`, `AP`, `US`, `Canada`, `Mexico` and `UK`. It
 * would also defeat `IX_requests_region` on the live list. Aliases belong where
 * text arrives from a human or a model; everything downstream compares
 * `CanonicalRegion`.
 *
 * ⚠️ THE COLLATION ALREADY FOLDS CASE. `SQL_Latin1_General_CP1_CI_AS` means
 * `region = 'NAM'` matches a row stored `nam` without any help. Case is NOT
 * what this module is for — spelling is.
 *
 * ⚠️ SYNC WITH: `server/constants/requestRegions.ts`. Byte-identical but for
 * this paragraph. The server's `rootDir: "./"` forbids a source file under
 * `server/` importing anything from `src/`, so these cannot be one module; they
 * are pinned by `server/constants/__tests__/requestRegions.sync.test.ts`.
 */

export const CANONICAL_REGIONS = ['NAM', 'EMEA', 'APAC'] as const;

export type CanonicalRegion = (typeof CANONICAL_REGIONS)[number];

/**
 * Every spelling that MEANS a canonical region, for write paths only.
 *
 * Keys are upper-cased and trimmed before lookup. Keep this list short and
 * defensible: each entry is a spelling we have actually seen or that a model is
 * told it may emit. It is not a place to be generous — every addition widens
 * what a supervisor becomes responsible for, for ever.
 */
export const REGION_ALIASES: ReadonlyMap<string, CanonicalRegion> = new Map([
  ['NAM', 'NAM'],
  ['NORTH AMERICA', 'NAM'],
  ['NA', 'NAM'],
  ['EMEA', 'EMEA'],
  ['EUROPE', 'EMEA'],
  ['EU', 'EMEA'],
  ['APAC', 'APAC'],
  ['ASIA', 'APAC'],
  ['ASIA-PACIFIC', 'APAC'],
  ['AP', 'APAC'],
] as const);

/**
 * Stored values that mean "no region", and must never match one.
 *
 * `[BLANK]` is not corruption — the audio and PDF import flows write it
 * deliberately when no region could be read (`ImportPreCreationModal.tsx`). It
 * is listed so `normalizeRegion` returns `null` for it explicitly rather than by
 * falling off the end, because the difference shows up in review.
 */
export const UNKNOWN_REGION_TOKENS: readonly string[] = ['', '[BLANK]'];

/** The canonical region a stored or typed value means, or `null`. */
export function normalizeRegion(
  raw: string | null | undefined,
): CanonicalRegion | null {
  if (raw === null || raw === undefined) return null;
  const token = raw.trim().toUpperCase();
  if (token === '' || UNKNOWN_REGION_TOKENS.includes(token)) return null;
  return REGION_ALIASES.get(token) ?? null;
}

/**
 * `region = N'NAM'`, for a predicate the query builder assembles as text.
 *
 * ⚠️ INTERPOLATION IS SAFE HERE AND ONLY HERE, FOR THE SAME REASON
 * `VERTIV_VISIBLE_STAGE_SQL` INTERPOLATES ITS STAGES: the value is a
 * `CanonicalRegion`, a closed vocabulary this module owns, and the type system
 * is what keeps arbitrary text out. The ALIASES are a different matter — they
 * describe data that arrives from humans and models, they never reach SQL, and
 * a caller-supplied region stays a bound parameter (`region = @region`).
 *
 * ⚠️ NOT `LOWER(region)`. The collation already folds case, and wrapping the
 * column in a function makes the predicate non-SARGable — which has been
 * quietly costing `IX_requests_region` on every supervisor's page of the
 * request list.
 */
export function regionMatchSql(
  region: CanonicalRegion,
  alias?: string,
): string {
  const column = alias ? `${alias}.region` : 'region';
  return `${column} = N'${region}'`;
}

export function isCanonicalRegion(value: unknown): value is CanonicalRegion {
  return (
    typeof value === 'string' &&
    (CANONICAL_REGIONS as readonly string[]).includes(value)
  );
}

/**
 * Do these two values name the same region?
 *
 * ⚠️ THE `null` ARM IS LOAD-BEARING AND IS NOT A STYLE CHOICE. Without it,
 * `[BLANK]` equals `''` equals `'Mars'`, and every supervisor is granted every
 * request whose region nobody could read — 29 of them today. Same shape, and the
 * same reason, as `isVertivVisibleStage`'s `if (!stage) return false`.
 */
export function sameRegion(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = normalizeRegion(a);
  return left !== null && left === normalizeRegion(b);
}

/**
 * The region a `Supervisor NAM` / `Supervisor EMEA` / `Supervisor APAC` role
 * makes somebody responsible for, or `null` for any other role.
 *
 * Replaces `role.split(' ')[1]` in `requestAuthorizationService` and
 * `internalIdentity`, which is the same parse written twice with a doc comment
 * promising they match.
 */
export function regionOfSupervisorRole(
  role: string | null | undefined,
): CanonicalRegion | null {
  if (!role) return null;
  const match = /^Supervisor\s+(\S+)$/.exec(role.trim());
  if (!match) return null;
  const region = match[1].toUpperCase();
  return isCanonicalRegion(region) ? region : null;
}

/**
 * The case table both copies are held to, by literal answers.
 *
 * Exported so the sync test can drive the client copy with the same inputs
 * without the two implementations ever being compared to each other — see that
 * file's header for why the mutual comparison is the useless shape.
 */
export const REGION_PARSE_CASES: readonly (readonly [
  string | null | undefined,
  CanonicalRegion | null,
])[] = [
  ['NAM', 'NAM'],
  ['nam', 'NAM'],
  ['  nam  ', 'NAM'],
  ['North America', 'NAM'],
  ['NORTH AMERICA', 'NAM'],
  ['NA', 'NAM'],
  ['EMEA', 'EMEA'],
  ['emea', 'EMEA'],
  ['Europe', 'EMEA'],
  ['EU', 'EMEA'],
  ['APAC', 'APAC'],
  ['apac', 'APAC'],
  ['Asia', 'APAC'],
  ['asia-pacific', 'APAC'],
  ['AP', 'APAC'],
  ['[BLANK]', null],
  ['[blank]', null],
  ['', null],
  ['   ', null],
  ['Mars', null],
  ['N', null],
  ['LATAM', null],
  [null, null],
  [undefined, null],
] as const;
